import { useMemo, useState } from 'react'
import { PageHeader } from '@/shell/AppShell'
import { CsvButton } from '@/components/CsvButton'
import { downloadCsv } from '@/lib/csv'

// A16.43 — Ma'lumot lug'ati (data dictionary). To'liq STATIK reference ekran (backend'siz):
// analitika metrikalari/maydonlari, turi, manba-jadval va ta'rifi. Modul-lokal — adapters.ts /
// i18n / screenStatus HOT emas, tegilmaydi. Bu ekran «single source of truth» hujjati:
// har metrika QAYSI jadval/ustundan hisoblanishi va aniq ta'rifi.

interface DictEntry {
  field: string
  type: string
  source: string
  definition: string
}
interface DictDomain {
  domain: string
  entries: DictEntry[]
}

const DICTIONARY: DictDomain[] = [
  {
    domain: 'Foydalanuvchilar',
    entries: [
      { field: 'DAU', type: 'agregat', source: 'user_devices.last_active_at', definition: "Kunlik faol foydalanuvchilar — oxirgi 24 soatda kamida bitta faol qurilma-sessiyasi bo'lganlar." },
      { field: 'WAU', type: 'agregat', source: 'user_devices.last_active_at', definition: "Haftalik faol foydalanuvchilar — oxirgi 7 kunda faol bo'lganlar (unikal)." },
      { field: 'MAU', type: 'agregat', source: 'user_devices.last_active_at', definition: "Oylik faol foydalanuvchilar — oxirgi 30 kunda faol bo'lganlar (unikal)." },
      { field: 'new_users', type: 'sanoq', source: 'users.created_at', definition: "Tanlangan davrda ro'yxatdan o'tgan yangi foydalanuvchilar (deleted_at IS NULL)." },
      { field: 'retention_dN', type: 'foiz', source: 'users.created_at + faollik', definition: "Ro'yxatdan o'tgandan N kun keyin qaytib kelgan kohorta ulushi (rolling D1/D7/D30)." },
      { field: 'churn_dN', type: 'foiz', source: 'retention', definition: "1 − retention_dN: kohortadan yo'qolgan foydalanuvchilar ulushi." },
    ],
  },
  {
    domain: 'Sessiya va qurilma',
    entries: [
      { field: 'session_duration', type: 'davomiylik', source: 'user_devices.created_at → COALESCE(revoked_at, last_active_at)', definition: "Sessiya umri: yaratilishdan bekor qilinishgacha (yoki oxirgi faollikkача). PII-siz — token maydonlari SELECT qilinmaydi." },
      { field: 'platform', type: 'kategoriya', source: 'user_devices.platform', definition: "Qurilma platformasi (ios / android / web / desktop) bo'yicha taqsimot." },
      { field: 'app_version', type: 'kategoriya', source: 'user_devices.app_version', definition: "Ilova/OS versiyasi bo'yicha faol qurilmalar taqsimoti." },
      { field: 'lang', type: 'kategoriya', source: 'user_settings.lang', definition: "Interfeys tili bo'yicha foydalanuvchilar taqsimoti." },
    ],
  },
  {
    domain: 'Faollik',
    entries: [
      { field: 'activity_matrix', type: 'heatmap', source: 'messages.created_at', definition: "Hafta-kun × soat bo'yicha xabar faolligi issiqlik xaritasi (24×7 hujayra)." },
      { field: 'message_volume', type: 'sanoq', source: 'messages', definition: "Davr bo'yicha yuborilgan xabarlar soni (chat turi bo'yicha ajratiladi)." },
    ],
  },
  {
    domain: 'Bot ekotizimi',
    entries: [
      { field: 'total_bots', type: 'sanoq', source: 'users.is_bot=true', definition: "Platformadagi jami botlar (deleted_at IS NULL)." },
      { field: 'official_bots', type: 'sanoq', source: 'users.is_official=true', definition: "Rasmiy (verified) botlar soni." },
      { field: 'gateway_bots', type: 'sanoq', source: 'bot_platform.bots', definition: "Bot-gateway orqali ro'yxatdan o'tgan botlar." },
      { field: 'mini_app_bots', type: 'sanoq', source: 'bot_platform.bots.has_main_app', definition: "Mini-App (main app) yoqilgan botlar." },
      { field: 'total_active_users', type: 'sanoq', source: 'bot_platform.bots.active_users_count', definition: "Barcha botlar bo'yicha faol foydalanuvchilar yig'indisi." },
    ],
  },
  {
    domain: 'Iqtisodiyot',
    entries: [
      { field: 'points_circulating', type: 'balans', source: 'points_balance', definition: "Muomaladagi jami ballar (barcha egalar balansi yig'indisi)." },
      { field: 'points_earned', type: 'oqim', source: 'points_ledger (earn)', definition: "Butun davr mobaynida ishlangan ballar (lifetime)." },
      { field: 'points_spent', type: 'oqim', source: 'points_ledger (spend)', definition: "Sarflangan ballar yig'indisi." },
      { field: 'arpu', type: 'nisbat', source: 'daromad / faol foydalanuvchi', definition: "O'rtacha daromad har bir foydalanuvchiga (Average Revenue Per User)." },
      { field: 'arppu', type: 'nisbat', source: "daromad / to'lovchi", definition: "O'rtacha daromad har bir to'lovchi foydalanuvchiga (Average Revenue Per Paying User)." },
    ],
  },
  {
    domain: 'Daromad va o‘sish',
    entries: [
      { field: 'ad_revenue', type: 'pul', source: 'ad_campaigns', definition: "Reklama kampaniyalaridan tushgan daromad." },
      { field: 'revenue_total', type: 'pul', source: 'ad_campaigns + market_trades + bot_star_charges', definition: "Barcha manbalardan umumiy daromad dashboard'i." },
      { field: 'invite_conversion', type: 'foiz', source: 'invite_links', definition: "Taklif havolalari samaradorligi: bosilgan/ro'yxatdan o'tgan nisbati." },
      { field: 'channel_growth', type: 'oqim', source: 'dialogs (channel) + dialog_members', definition: "Kanal a'zolari o'sishi vaqt qatori." },
    ],
  },
  {
    domain: 'Moderatsiya va support',
    entries: [
      { field: 'moderation_kpi', type: 'agregat', source: 'reports', definition: "Shikoyatlar hajmi, hal qilish vaqti va qarorlar taqsimoti." },
      { field: 'support_kpi', type: 'agregat', source: 'support_tickets', definition: "Support tiketlari: ochiq/yopiq, birinchi javob vaqti, SLA." },
    ],
  },
]

const STR = {
  title: "Ma’lumot lug‘ati",
  note: "Analitika metrikalari va maydonlarining yagona ta’rif manbasi (single source of truth): tur, manba-jadval va aniq hisoblash ta’rifi.",
  search: 'Metrika yoki maydon qidirish…',
  field: 'Maydon / Metrika',
  type: 'Tur',
  source: 'Manba (jadval/ustun)',
  definition: "Ta’rif",
  empty: 'Mos keladigan yozuv topilmadi',
} as const

/** A16.43 — Ma'lumot lug'ati (statik reference, qidiruv + CSV eksport). */
export function DataDictionary() {
  const [q, setQ] = useState('')

  const filtered = useMemo<DictDomain[]>(() => {
    const query = q.trim().toLowerCase()
    if (!query) return DICTIONARY
    return DICTIONARY.map((d) => ({
      domain: d.domain,
      entries: d.entries.filter(
        (e) =>
          e.field.toLowerCase().includes(query) ||
          e.definition.toLowerCase().includes(query) ||
          e.source.toLowerCase().includes(query),
      ),
    })).filter((d) => d.entries.length > 0)
  }, [q])

  const handleExport = () => {
    const rows: (string | number)[][] = [[STR.field, STR.type, STR.source, STR.definition, 'Domen']]
    for (const d of DICTIONARY) {
      for (const e of d.entries) rows.push([e.field, e.type, e.source, e.definition, d.domain])
    }
    downloadCsv('data-dictionary', rows)
  }

  const anyResult = filtered.some((d) => d.entries.length > 0)

  return (
    <>
      <PageHeader
        title={STR.title}
        screenId="A16.43"
        description={STR.note}
        actions={<CsvButton onClick={handleExport} />}
      />
      <div className="min-h-0 flex-1 space-y-4 overflow-auto p-3">
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={STR.search}
          className="h-8 w-full max-w-md rounded-md border border-border-default bg-surface-1 px-2.5 text-sm text-primary placeholder:text-tertiary focus:border-border-strong focus:outline-none"
        />
        {!anyResult ? (
          <p className="py-8 text-center text-sm text-tertiary">{STR.empty}</p>
        ) : (
          filtered.map((d) => (
            <div key={d.domain} className="max-w-4xl overflow-hidden rounded-lg border border-border-default bg-surface-2">
              <div className="border-b border-border-default bg-surface-1 px-3 py-2 text-sm font-semibold text-primary">
                {d.domain}
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border-subtle text-left text-xs text-tertiary">
                    <th className="w-40 px-3 py-2 font-medium">{STR.field}</th>
                    <th className="w-24 px-3 py-2 font-medium">{STR.type}</th>
                    <th className="w-56 px-3 py-2 font-medium">{STR.source}</th>
                    <th className="px-3 py-2 font-medium">{STR.definition}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border-subtle align-top">
                  {d.entries.map((e) => (
                    <tr key={e.field} className="hover:bg-surface-1">
                      <td className="px-3 py-2 font-mono text-xs text-primary">{e.field}</td>
                      <td className="px-3 py-2 text-secondary">{e.type}</td>
                      <td className="px-3 py-2 font-mono text-xs text-tertiary">{e.source}</td>
                      <td className="px-3 py-2 text-secondary">{e.definition}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))
        )}
      </div>
    </>
  )
}
